package com.fantasytenis.web.screens.auth

import androidx.compose.runtime.*
import com.fantasytenis.shared.core.utils.SharedData
import com.fantasytenis.web.components.AvatarDisplay
import com.fantasytenis.web.components.AvatarSize
import com.fantasytenis.web.components.Button
import com.fantasytenis.web.components.TextField
import com.fantasytenis.web.di.DependencyContainer
import com.fantasytenis.web.theme.Colors
import com.fantasytenis.web.theme.h2Style
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * Full-screen modal shown to existing users who don't have a username.
 * Cannot be dismissed until a username is set.
 */
@Composable
fun UsernameSetupScreen(
    onComplete: () -> Unit
) {
    val currentUser by SharedData.currentUser.collectAsState()
    val scope = rememberCoroutineScope()

    var username by remember { mutableStateOf("") }
    var isLoading by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    var usernameValidation by remember { mutableStateOf(UsernameValidation.validateFormat("")) }
    var usernameCheckJob by remember { mutableStateOf<Job?>(null) }

    // Real-time username validation with debounced API check
    LaunchedEffect(username) {
        usernameCheckJob?.cancel()
        val formatValidation = UsernameValidation.validateFormat(username)
        usernameValidation = formatValidation

        if (formatValidation.isFormatValid) {
            usernameCheckJob = scope.launch {
                delay(500) // Debounce 500ms
                DependencyContainer.authRepository.checkUsernameAvailable(username.lowercase())
                    .onSuccess { result ->
                        usernameValidation = UsernameValidation(
                            isFormatValid = true,
                            isChecking = false,
                            isAvailable = result.available,
                            suggestion = result.suggestion.takeIf { it.isNotBlank() },
                            errorMessage = if (!result.available) "Username is already taken" else null
                        )
                    }
                    .onFailure {
                        usernameValidation = formatValidation.copy(isChecking = false)
                    }
            }
        }
    }

    // Full-screen overlay
    Div({
        style {
            position(Position.Fixed)
            top(0.px)
            left(0.px)
            right(0.px)
            bottom(0.px)
            backgroundColor(Colors.Background)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            padding(24.px)
            property("z-index", "9999")
        }
    }) {
        Div({
            style {
                backgroundColor(Colors.Surface)
                borderRadius(16.px)
                padding(48.px)
                property("box-shadow", "0 4px 24px rgba(0, 0, 0, 0.15)")
                maxWidth(480.px)
                width(100.percent)
                textAlign("center")
            }
        }) {
            // Welcome message
            H2({
                style {
                    h2Style()
                    marginBottom(16.px)
                    color(Colors.Primary)
                }
            }) {
                Text("Welcome to Baseline!")
            }

            P({
                style {
                    fontSize(16.px)
                    color(Colors.TextSecondary)
                    marginBottom(32.px)
                    lineHeight("1.5")
                }
            }) {
                Text("Choose a unique username to represent you on leaderboards and across the app.")
            }

            // Current user avatar (if they have one)
            currentUser?.let { user ->
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        justifyContent(JustifyContent.Center)
                        marginBottom(24.px)
                    }
                }) {
                    AvatarDisplay(
                        avatarUrl = user.avatarUrl,
                        initials = user.initials,
                        size = AvatarSize.LARGE
                    )
                }

                P({
                    style {
                        fontSize(14.px)
                        color(Colors.TextSecondary)
                        marginBottom(24.px)
                    }
                }) {
                    Text("Hi ${user.firstName}! Let's set up your username.")
                }
            }

            // Username input
            Div({
                style {
                    marginBottom(16.px)
                    textAlign("left")
                }
            }) {
                TextField(
                    value = username,
                    onValueChange = { username = it.lowercase().filter { c -> c.isLetterOrDigit() || c == '_' } },
                    label = "Username",
                    placeholder = "johndoe",
                    error = usernameValidation.errorMessage
                )

                // Username status indicator
                if (username.isNotBlank()) {
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            gap(8.px)
                            marginTop(8.px)
                            fontSize(13.px)
                        }
                    }) {
                        when {
                            usernameValidation.isChecking -> {
                                Span({ style { color(Colors.TextSecondary) } }) {
                                    Text("Checking availability...")
                                }
                            }
                            usernameValidation.isAvailable == true -> {
                                Span({ style { color(Colors.Success) } }) {
                                    Text("\u2713 @${username.lowercase()} is available!")
                                }
                            }
                            usernameValidation.isAvailable == false && usernameValidation.suggestion != null -> {
                                Span({ style { color(Colors.TextSecondary) } }) {
                                    Text("Try: ")
                                    A(
                                        href = "#",
                                        attrs = {
                                            onClick { e ->
                                                e.preventDefault()
                                                username = usernameValidation.suggestion!!
                                            }
                                            style {
                                                color(Colors.Accent)
                                                fontWeight(500)
                                            }
                                        }
                                    ) {
                                        Text("@${usernameValidation.suggestion}")
                                    }
                                }
                            }
                        }
                    }
                }
            }

            // Error message
            error?.let { errorMessage ->
                P({
                    style {
                        color(Colors.Error)
                        fontSize(14.px)
                        marginBottom(16.px)
                    }
                }) {
                    Text(errorMessage)
                }
            }

            // Continue button
            Button(
                text = if (isLoading) "Saving..." else "Continue",
                onClick = {
                    if (!isLoading && usernameValidation.isValid) {
                        isLoading = true
                        error = null

                        scope.launch {
                            DependencyContainer.authRepository.updateUser(
                                username = username.lowercase()
                            ).onSuccess { updatedUser ->
                                SharedData.currentUser.value = updatedUser
                                isLoading = false
                                onComplete()
                            }.onFailure { e ->
                                isLoading = false
                                error = e.message ?: "Failed to save username"
                            }
                        }
                    }
                },
                enabled = !isLoading && usernameValidation.isValid,
                fullWidth = true
            )

            // Info text
            P({
                style {
                    fontSize(12.px)
                    color(Colors.TextSecondary)
                    marginTop(16.px)
                }
            }) {
                Text("You can change your username later in Account Settings.")
            }
        }
    }
}
